import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  Eye,
  EyeOff,
  AlertCircle,
  Grid3X3,
  Loader2,
} from 'lucide-react'
import { useAttemptResponses, useQuestion } from '../hooks/useMockTest'
import ReviewQuestionViewer from '../components/ReviewQuestionViewer'
import ReviewQuestionPicker from '../components/ReviewQuestionPicker'
import type { QuestionResponseDetailResponse } from '../types'

type SubjectFilter = string | null
type CorrectnessFilter = 'all' | 'correct' | 'incorrect' | 'unanswered'

export default function AttemptReviewPage() {
  const { attemptId } = useParams<{ testId: string; attemptId: string }>()
  const navigate = useNavigate()

  const [currentIndex, setCurrentIndex] = useState(0)
  const [showSolution, setShowSolution] = useState(true)
  const [showQuestionPicker, setShowQuestionPicker] = useState(false)
  const [subjectFilter, setSubjectFilter] = useState<SubjectFilter>(null)
  const [correctnessFilter, setCorrectnessFilter] = useState<CorrectnessFilter>('all')

  // Fetch all responses for the attempt
  const {
    data: responsesData,
    isLoading: isLoadingResponses,
    isError: isResponsesError,
  } = useAttemptResponses(attemptId || '')

  const allResponses: QuestionResponseDetailResponse[] = responsesData?.data || []

  // Apply filters
  const filteredResponses = allResponses.filter((r) => {
    if (subjectFilter && r.subjectCode !== subjectFilter) return false
    if (correctnessFilter === 'correct' && !r.isCorrect) return false
    if (correctnessFilter === 'incorrect' && (!r.isAttempted || r.isCorrect)) return false
    if (correctnessFilter === 'unanswered' && r.isAttempted) return false
    return true
  })

  const totalQuestions = filteredResponses.length
  const safeCurrentIndex = Math.max(0, Math.min(currentIndex, totalQuestions - 1))
  const currentResponse = totalQuestions > 0 ? filteredResponses[safeCurrentIndex] : undefined

  // Fetch current question data
  const {
    data: questionData,
    isLoading: isLoadingQuestion,
  } = useQuestion(currentResponse?.questionId || '', !!currentResponse?.questionId)

  const currentQuestion = questionData?.data

  // Get unique subjects for filter
  const subjects = [...new Set(allResponses.map((r) => r.subjectCode).filter(Boolean))] as string[]

  // Reset index when filters change
  useEffect(() => {
    setCurrentIndex(0)
  }, [subjectFilter, correctnessFilter])

  // Ensure currentIndex stays in bounds
  useEffect(() => {
    if (totalQuestions > 0 && currentIndex !== safeCurrentIndex) {
      setCurrentIndex(safeCurrentIndex)
    }
  }, [currentIndex, safeCurrentIndex, totalQuestions])

  // Navigation handlers
  const goToFirst = () => setCurrentIndex(0)
  const goToPrev = () => setCurrentIndex((i) => Math.max(0, i - 1))
  const goToNext = () => setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1))
  const goToLast = () => setCurrentIndex(totalQuestions - 1)

  const handleQuestionSelect = (index: number) => {
    setCurrentIndex(index)
    setShowQuestionPicker(false)
  }

  const goBack = () => navigate(-1)

  // Loading state
  if (isLoadingResponses) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading attempt responses...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (isResponsesError || allResponses.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-800">
            {isResponsesError ? 'Failed to load responses' : 'No responses found'}
          </h2>
          <p className="text-gray-600 mt-1 mb-4">
            {isResponsesError
              ? 'An error occurred while loading the attempt data.'
              : 'This attempt has no question responses to review.'}
          </p>
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header - Fixed */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium hidden sm:inline">Back</span>
          </button>

          <div className="text-center min-w-0 px-2">
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Review Questions</h1>
            <p className="text-xs text-gray-500">
              {totalQuestions} Qs{subjectFilter || correctnessFilter !== 'all' ? ' (filtered)' : ''}
            </p>
          </div>

          <button
            onClick={() => setShowSolution(!showSolution)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors flex-shrink-0 ${
              showSolution
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showSolution ? (
              <>
                <EyeOff className="w-4 h-4" />
                <span className="hidden sm:inline">Hide</span>
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Show</span>
              </>
            )}
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="px-3 sm:px-4 pb-2 space-y-1.5 sm:space-y-0">
          {/* Mobile: 2 rows | Desktop: single row */}
          {/* Row 1: Correctness Filters */}
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide">
            {(['all', 'correct', 'incorrect', 'unanswered'] as CorrectnessFilter[]).map((filter) => {
              const labels: Record<CorrectnessFilter, string> = {
                all: 'All',
                correct: 'Correct',
                incorrect: 'Wrong',
                unanswered: 'Skipped',
              }
              const colors: Record<CorrectnessFilter, string> = {
                all: correctnessFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600',
                correct: correctnessFilter === 'correct' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600',
                incorrect: correctnessFilter === 'incorrect' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600',
                unanswered: correctnessFilter === 'unanswered' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-600',
              }
              return (
                <button
                  key={filter}
                  onClick={() => setCorrectnessFilter(filter)}
                  className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${colors[filter]}`}
                >
                  {labels[filter]}
                </button>
              )
            })}

            {/* Desktop: subject filters inline after a separator */}
            {subjects.length > 1 && (
              <>
                <span className="text-gray-300 flex-shrink-0 hidden sm:inline">|</span>
                <button
                  onClick={() => setSubjectFilter(null)}
                  className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors hidden sm:block ${
                    !subjectFilter ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  All
                </button>
                {subjects.map((subject) => (
                  <button
                    key={subject}
                    onClick={() => setSubjectFilter(subject)}
                    className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors hidden sm:block ${
                      subjectFilter === subject ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {subject}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Row 2 (mobile only): Subject Filters */}
          {subjects.length > 1 && (
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide sm:hidden">
              <button
                onClick={() => setSubjectFilter(null)}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  !subjectFilter ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                All
              </button>
              {subjects.map((subject) => (
                <button
                  key={subject}
                  onClick={() => setSubjectFilter(subject)}
                  className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    subjectFilter === subject ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 min-h-0 overflow-y-auto">
        {totalQuestions === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-800">
                No questions match filters
              </h2>
              <p className="text-gray-600 mt-1">Try adjusting your filters.</p>
            </div>
          </div>
        ) : isLoadingQuestion ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : currentQuestion && currentResponse ? (
          <div className="h-full">
            <ReviewQuestionViewer
              question={currentQuestion}
              response={currentResponse}
              questionNumber={safeCurrentIndex + 1}
              totalQuestions={totalQuestions}
              showSolution={showSolution}
            />
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-800">
                Question not found
              </h2>
            </div>
          </div>
        )}
      </main>

      {/* Floating Question Picker Button */}
      {totalQuestions > 0 && (
        <button
          onClick={() => setShowQuestionPicker(true)}
          className="fixed bottom-20 right-3 sm:bottom-24 sm:right-4 w-11 h-11 sm:w-12 sm:h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center z-20"
          aria-label="Select question"
        >
          <Grid3X3 className="w-5 h-5" />
        </button>
      )}

      {/* Question Picker Modal */}
      {showQuestionPicker && (
        <ReviewQuestionPicker
          responses={filteredResponses}
          currentIndex={safeCurrentIndex}
          onSelect={handleQuestionSelect}
          onClose={() => setShowQuestionPicker(false)}
        />
      )}

      {/* Footer Navigation - Fixed */}
      <nav className="flex-shrink-0 bg-white border-t border-gray-200 px-2 py-2 sm:px-4 sm:py-3 z-10">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button
            onClick={goToFirst}
            disabled={safeCurrentIndex === 0 || totalQuestions === 0}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 sm:px-3 sm:py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">First</span>
          </button>

          <button
            onClick={goToPrev}
            disabled={safeCurrentIndex === 0 || totalQuestions === 0}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 sm:px-3 sm:py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">Prev</span>
          </button>

          {/* Page indicator */}
          <div className="text-center min-w-[4rem]">
            <span className="text-base sm:text-lg font-bold text-gray-900">
              {totalQuestions > 0 ? safeCurrentIndex + 1 : 0}
            </span>
            <span className="text-gray-400 mx-0.5 sm:mx-1">/</span>
            <span className="text-base sm:text-lg text-gray-500">{totalQuestions}</span>
          </div>

          <button
            onClick={goToNext}
            disabled={safeCurrentIndex >= totalQuestions - 1 || totalQuestions === 0}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 sm:px-3 sm:py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">Next</span>
          </button>

          <button
            onClick={goToLast}
            disabled={safeCurrentIndex >= totalQuestions - 1 || totalQuestions === 0}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 sm:px-3 sm:py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsRight className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="text-[10px] sm:text-xs font-medium">Last</span>
          </button>
        </div>
      </nav>
    </div>
  )
}
